import { useState } from "react";
import { ChevronDown, ChevronRight, PanelRightOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

import { CorrectedValue } from "@/components/agents/configuration-server/CorrectedValue";
import { OrgUnitRowActions } from "@/components/agents/configuration-server/OrgUnitRowActions";
import {
  ORG_UNIT_ACCENT,
  originAccent,
} from "@/components/agents/configuration-server/orgUnitAccent";
import { isDispatching } from "@/components/agents/configuration-server/orgUnitState";
import { RowActionButton } from "@/components/brand/RowActionButton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  effectiveOrgId,
  extractedValueOf,
  orgUnitTypeLabelKey,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";
import { cn } from "@/lib/utils";

/** Id e nome da linha — com o valor extraído riscado quando o consultor
 *  corrigiu uma unidade da extração. */
function RowLabel({
  node,
  depth,
  unitTypeLabel,
}: {
  node: OrgTreeNode;
  depth: number;
  unitTypeLabel: string;
}) {
  const { t } = useTranslation();
  const extractedId = extractedValueOf(node, "id");
  const extractedName = extractedValueOf(node, "name");
  const idChip = (value: string) => (
    <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[11px] text-[#08254c]">
      {value}
    </span>
  );
  const name = node.name ?? <span className="text-[#7b8799]">{t("name not extracted")}</span>;

  return (
    <>
      {extractedId ? (
        <CorrectedValue
          extracted={extractedId.value}
          current={idChip(effectiveOrgId(node))}
          emptyLabel="—"
          className="font-mono text-[11px]"
        />
      ) : (
        idChip(node.id)
      )}
      <span
        className={
          depth === 0 ? "text-[13px] font-semibold text-[#08254c]" : "text-[13px] text-[#08254c]"
        }
      >
        {extractedName ? (
          <CorrectedValue
            extracted={extractedName.value}
            current={name}
            emptyLabel={t("name not extracted")}
          />
        ) : (
          name
        )}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {unitTypeLabel}
      </span>
    </>
  );
}

function ListRow({
  node,
  depth,
  expandedKeys,
  onToggle,
  onSelectNode,
  actions,
}: {
  node: OrgTreeNode;
  depth: number;
  expandedKeys: Set<string>;
  onToggle: (key: string) => void;
  onSelectNode: (node: OrgTreeNode) => void;
  actions: RowActionHandlers;
}) {
  const { t } = useTranslation();
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedKeys.has(node.key);
  const unitTypeLabel = t(
    orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName),
    {
      defaultValue: node.levelName,
    },
  );

  function handleToggleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle(node.key);
    }
  }

  return (
    <>
      <div
        data-testid={`org-node-${node.key}`}
        data-origin={node.origin}
        className={cn(
          // Faixa lateral = procedência; fundo verde = já cadastrada no CBC
          // (legenda em `OrgOriginLegend`).
          "flex items-center gap-2 border-b border-l-[3px] border-b-[#f2f4f7] py-2 pr-2 last:border-b-0",
          originAccent(node.origin).stripe,
          node.migrationStatus === "completed" && ORG_UNIT_ACCENT.registered,
          node.scopeVerdict === "out_of_scope" && "opacity-50",
        )}
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {actions.selection && (
          <Checkbox
            aria-label={t("Select {{name}}", { name: effectiveOrgId(node) })}
            checked={actions.selection.selectedKeys.has(node.key)}
            onCheckedChange={(checked) => actions.selection?.onToggle([node], checked === true)}
          />
        )}
        {hasChildren ? (
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => onToggle(node.key)}
            onKeyDown={handleToggleKeyDown}
            aria-expanded={isExpanded}
          >
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#7b8799]">
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
            <RowLabel node={node} depth={depth} unitTypeLabel={unitTypeLabel} />
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="flex h-3.5 w-3.5 shrink-0" />
            <RowLabel node={node} depth={depth} unitTypeLabel={unitTypeLabel} />
          </div>
        )}
        <OrgUnitRowActions
          node={node}
          cbcConnected={actions.cbcConnected}
          runInProgressLabel={actions.runInProgressLabel}
          runBlockedReason={actions.runBlockedReason}
          dispatching={isDispatching(actions, node)}
          hideExecution={actions.hideRun}
          onRun={actions.hideRun ? undefined : actions.onRunNode}
          onRequestVerdict={actions.hideVerdict ? undefined : actions.onRequestVerdict}
          hideCrud={actions.hideCrud}
          onEdit={actions.onEditUnit}
          onDelete={actions.onDeleteUnit}
        />
        <RowActionButton
          label={t("View details for {{id}}", { id: effectiveOrgId(node) })}
          icon={<PanelRightOpen />}
          onClick={() => onSelectNode(node)}
        />
      </div>
      {hasChildren &&
        isExpanded &&
        node.children.map((child) => (
          <ListRow
            key={child.key}
            node={child}
            depth={depth + 1}
            expandedKeys={expandedKeys}
            onToggle={onToggle}
            onSelectNode={onSelectNode}
            actions={actions}
          />
        ))}
    </>
  );
}

/** Tudo que a linha precisa para montar as ações por estado da unidade. */
export interface RowActionHandlers {
  cbcConnected: boolean;
  runInProgressLabel?: string;
  /** Etapa 3: motivo de outra execução no projeto travar o play. */
  runBlockedReason?: string | null;
  /** `item_type:item_key` das unidades cujo play acabou de ser clicado e
   *  cujo status "running" ainda não chegou — o play delas já gira. */
  dispatchingUnitKeys?: ReadonlySet<string>;
  onRunNode?: (node: OrgTreeNode) => void;
  selection?: OrgSelection;
  hideRun?: boolean;
  /** Modo execução: as linhas não mostram os botões de veredito. */
  hideVerdict?: boolean;
  /** Modo execução: as linhas não mostram editar nem excluir. */
  hideCrud?: boolean;
  onRequestVerdict: (node: OrgTreeNode, verdict: Exclude<ScopeVerdict, "pending">) => void;
  onEditUnit: (node: OrgTreeNode) => void;
  onDeleteUnit: (node: OrgTreeNode) => void;
}

export interface OrgSelection {
  selectedKeys: ReadonlySet<string>;
  onToggle: (nodes: OrgTreeNode[], checked: boolean) => void;
}

const noop = () => {};

/**
 * Colapsada por padrão: a lista cresce sob demanda, um nó por vez.
 */
export function OrgStructureListView({
  roots,
  onSelectNode,
  // Opcionais com defaults inertes: os testes de layout deste componente não
  // montam o wiring de execução/escopo. Todo chamador real
  // (`OrgStructureTree.tsx`) passa tudo.
  onRunNode = noop,
  onRequestVerdict = noop,
  onEditUnit = noop,
  onDeleteUnit = noop,
  cbcConnected = true,
  runInProgressLabel,
  runBlockedReason,
  dispatchingUnitKeys,
  selection,
  hideRun,
  hideVerdict,
  hideCrud,
}: {
  roots: OrgTreeNode[];
  onSelectNode: (node: OrgTreeNode) => void;
  onRunNode?: (node: OrgTreeNode) => void;
  onRequestVerdict?: RowActionHandlers["onRequestVerdict"];
  onEditUnit?: (node: OrgTreeNode) => void;
  onDeleteUnit?: (node: OrgTreeNode) => void;
  cbcConnected?: boolean;
  runInProgressLabel?: string;
  runBlockedReason?: string | null;
  dispatchingUnitKeys?: ReadonlySet<string>;
  selection?: OrgSelection;
  hideRun?: boolean;
  /** Esconde ✓ e ⊘ de cada linha/cartão (modo execução). */
  hideVerdict?: boolean;
  /** Esconde editar e excluir de cada linha/cartão (modo execução). */
  hideCrud?: boolean;
}) {
  const actions: RowActionHandlers = {
    cbcConnected,
    runInProgressLabel,
    runBlockedReason,
    dispatchingUnitKeys,
    onRunNode,
    onRequestVerdict,
    onEditUnit,
    onDeleteUnit,
    selection,
    hideRun,
    hideVerdict,
    hideCrud,
  };
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  function toggle(key: string) {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div className="px-4">
      {roots.map((node) => (
        <ListRow
          key={node.key}
          node={node}
          depth={0}
          expandedKeys={expandedKeys}
          onToggle={toggle}
          onSelectNode={onSelectNode}
          actions={actions}
        />
      ))}
    </div>
  );
}
